import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { AuthProvider, useAuth } from './services/AuthContext';
import ErrorBoundary from './components/Common/ErrorBoundary'; // ✅ 02MARS2026: Protection crashes globaux
import SuperAdminLayout from './layouts/SuperAdminLayout';
import Login from './components/Auth/Login';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import IntervenantLayout from './layouts/IntervenantLayout';
import ClientLayout from './components/Client/ClientLayout';
import { getUserPreferences, applyUserPreferences } from './services/userPreferences';
import { ThemePreferencesProvider } from './contexts/ThemePreferencesContext';
import UserPreferencesSync from './components/Common/UserPreferencesSync';
import I18nLanguageSync from './components/I18nLanguageSync';
import { publicLegalRouteElements } from './routes/publicLegalRouteElements';
import RoleHomeRedirect from './components/Common/RoleHomeRedirect';
import DeferredMessagingWidget from './components/Messaging/DeferredMessagingWidget';
import DeployMaintenanceNotice from './components/Common/DeployMaintenanceNotice';
import SpeedInsightsRoute from './components/Common/SpeedInsightsRoute';
import './App.css';
import { useTranslation } from 'react-i18next';

const Dashboard = React.lazy(() => import('./pages/Dashboard/Dashboard'));
const Admins = React.lazy(() => import('./pages/SuperAdmin/Admins'));
const Clients = React.lazy(() => import('./pages/SuperAdmin/Clients'));
const Intervenants = React.lazy(() => import('./pages/SuperAdmin/Intervenants'));
const Rapports = React.lazy(() => import('./pages/SuperAdmin/Rapports'));
const AccountSettingsPage = React.lazy(() => import('./pages/Common/AccountSettingsPage'));
const AdminDashboard = React.lazy(() => import('./pages/Admin/AdminDashboard'));
const AdminClients = React.lazy(() => import('./pages/Admin/AdminClients'));
const AdminIntervenants = React.lazy(() => import('./pages/Admin/AdminIntervenants'));
const AdminRapports = React.lazy(() => import('./pages/Admin/AdminRapports'));
const IntervenantDashboard = React.lazy(() => import('./pages/Intervenant/IntervenantDashboard'));
const InterventionsListPage = React.lazy(() => import('./pages/Common/InterventionsListPage'));
const InterventionDetailPage = React.lazy(() => import('./pages/Common/InterventionDetailPage'));
const IntervenantRapports = React.lazy(() => import('./pages/Intervenant/IntervenantRapports'));
const IntervenantRapportNew = React.lazy(() => import('./pages/Intervenant/IntervenantRapportNew'));
const IntervenantDisponibilites = React.lazy(() => import('./pages/Intervenant/IntervenantDisponibilites'));
const ClientDashboard = React.lazy(() => import('./pages/Client/ClientDashboard'));
const ClientEquipements = React.lazy(() => import('./pages/Client/ClientEquipements'));
const ClientRapports = React.lazy(() => import('./pages/Client/ClientRapports'));
const ClientSites = React.lazy(() => import('./pages/Client/ClientSites'));
const BackofficeInterventionsPage = React.lazy(() => import('./pages/Common/BackofficeInterventionsPage'));
const DemoTypesPage = React.lazy(() => import('./pages/DemoTypesPage'));

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
`;

const RouteFallback = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40vh;
  color: var(--ucp-text-muted, inherit);
`;

function RouteSuspense({ children, label }) {
  return (
    <Suspense fallback={<RouteFallback>{label}</RouteFallback>}>
      {children}
    </Suspense>
  );
}

// Composant PrivateRoute pour protéger les routes selon les rôles
function PrivateRoute({ children, allowedRoles }) {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirection selon le rôle de l'utilisateur
    switch (user.role) {
      case 'superadmin':
        return <Navigate to="/dashboard" replace />;
      case 'admin':
        return <Navigate to="/admin/dashboard" replace />;
      case 'intervenant':
        return <Navigate to="/intervenant" replace />;
      case 'client':
        return <Navigate to="/client" replace />;
      default:
        return <Navigate to="/" replace />;
    }
  }

  return children;
}

function AppContent() {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const loadingLabel = t('common.loading');

  useEffect(() => {
    if (!user) return;
    applyUserPreferences(getUserPreferences());
  }, [user]);

  if (loading) {
    return <LoadingContainer>{loadingLabel}</LoadingContainer>;
  }

  if (!user) {
    return (
      <RouteSuspense label={loadingLabel}>
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {publicLegalRouteElements}
          <Route path="/demo-types" element={<DemoTypesPage />} /> {/* ✅ 17FEV2026: Démo dropdown */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </RouteSuspense>
    );
  }

  return (
    <RouteSuspense label={loadingLabel}>
      <DeployMaintenanceNotice />
      <DeferredMessagingWidget />
      {/* Routes SuperAdmin avec SuperAdminLayout (AppLayout centralisé) */}
      {user.role === 'superadmin' && (
        <ErrorBoundary>
        <Routes>
          {publicLegalRouteElements}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute allowedRoles={['superadmin']}>
                <SuperAdminLayout />
              </PrivateRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="admins"        element={<Admins />} />
            <Route path="clients"       element={<Clients />} />
            <Route path="intervenants"  element={<Intervenants />} />
            <Route path="interventions" element={<BackofficeInterventionsPage role="superadmin" />} />
            <Route path="rapports"      element={<Rapports />} />
            <Route path="paramètres"    element={<AccountSettingsPage role="superadmin" />} />
            <Route path="*"             element={<Navigate to="/dashboard" replace />} />
          </Route>
          <Route path="*" element={<RoleHomeRedirect />} />
        </Routes>
        </ErrorBoundary>
      )}

      {/* Routes Admin sans MainLayout (AdminLayout intégré dans pages) */}
      {user.role === 'admin' && (
        <ErrorBoundary>
        <Routes>
          {publicLegalRouteElements}
          <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
          <Route 
            path="/admin/dashboard" 
            element={
              <PrivateRoute allowedRoles={['admin']}>
                <AdminDashboard />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/admin/clients" 
            element={
              <PrivateRoute allowedRoles={['admin']}>
                <AdminClients />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/admin/intervenants" 
            element={
              <PrivateRoute allowedRoles={['admin']}>
                <AdminIntervenants />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/admin/interventions" 
            element={
              <PrivateRoute allowedRoles={['admin']}>
                <BackofficeInterventionsPage role="admin" />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/admin/rapports" 
            element={
              <PrivateRoute allowedRoles={['admin']}>
                <AdminRapports />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/admin/parametres" 
            element={
              <PrivateRoute allowedRoles={['admin']}>
                <AccountSettingsPage role="admin" />
              </PrivateRoute>
            } 
          />
          <Route path="*" element={<RoleHomeRedirect />} />
        </Routes>
        </ErrorBoundary>
      )}

      {/* Routes Intervenant avec IntervenantLayout */}
      {user.role === 'intervenant' && (
        <ErrorBoundary>
        <Routes>
          {publicLegalRouteElements}
          <Route path="/" element={<Navigate to="/intervenant" replace />} />
          <Route 
            path="/intervenant" 
            element={
              <PrivateRoute allowedRoles={['intervenant']}>
                <IntervenantLayout />
              </PrivateRoute>
            }
          >
            <Route index element={<IntervenantDashboard />} />
            <Route path="interventions" element={<InterventionsListPage role="intervenant" />} />
            <Route path="interventions/:id" element={<InterventionDetailPage role="intervenant" />} />
            <Route path="rapports" element={<IntervenantRapports />} />
            <Route path="rapports/nouveau" element={<IntervenantRapportNew />} /> {/* ✅ 07FEV2026: Route création rapport */}
            <Route path="disponibilites" element={<IntervenantDisponibilites />} />
            <Route path="parametres" element={<AccountSettingsPage role="intervenant" />} />
            <Route path="*" element={<Navigate to="/intervenant" replace />} />
          </Route>
          <Route path="*" element={<RoleHomeRedirect />} />
        </Routes>
        </ErrorBoundary>
      )}

      {/* Routes Client avec ClientLayout parent centralisé */}
      {user.role === 'client' && (
        <ErrorBoundary>
        <Routes>
          {publicLegalRouteElements}
          <Route path="/" element={<Navigate to="/client" replace />} />
          <Route
            path="/client"
            element={
              <PrivateRoute allowedRoles={['client']}>
                <ClientLayout />
              </PrivateRoute>
            }
          >
            <Route index element={<ClientDashboard />} />
            <Route path="sites" element={<ClientSites />} />
            <Route path="interventions" element={<InterventionsListPage role="client" />} />
            <Route path="interventions/:id" element={<InterventionDetailPage role="client" />} />
            <Route path="equipements" element={<ClientEquipements />} />
            <Route path="rapports" element={<ClientRapports />} />
            <Route path="profil" element={<AccountSettingsPage role="client" />} />
            <Route path="*" element={<Navigate to="/client" replace />} />
          </Route>
          <Route path="*" element={<RoleHomeRedirect />} />
        </Routes>
        </ErrorBoundary>
      )}
    </RouteSuspense>
  );
}


function App() {
  return (
    <AuthProvider>
      <ThemePreferencesProvider>
        <I18nLanguageSync />
        <UserPreferencesSync />
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          <ErrorBoundary>
            <AppContent />
            <SpeedInsightsRoute />
          </ErrorBoundary>
        </Router>
      </ThemePreferencesProvider>
    </AuthProvider>
  );
}

export default App;
